import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Flame, Trophy, Dumbbell, CheckCircle2, CalendarCheck, Share2 } from "lucide-react";
import { StreakCard } from "@/components/StreakCard";
import { StreakLeaderboard } from "@/components/StreakLeaderboard";
import { BadgeWall } from "@/components/BadgeWall";
import { api, errMsg, fmt, fmtDate, HL_KIND } from "@/lib/api";
import { SignOut } from "@/components/SignOut";
import { SessionActions } from "@/components/SessionActions";
import { ProgressChart } from "@/components/ProgressChart";
import { MetricForm } from "@/components/MetricForm";
import { Avatar } from "@/components/Avatar";
import { ProgressPhotos } from "@/components/ProgressPhotos";
import { ClientLibrary } from "@/components/ExerciseLibrary";
import { WeeklyPlan } from "@/components/WeeklyPlan";
import { CheckInCard } from "@/components/CheckIn";
import { CheckinTrends } from "@/components/CheckinTrends";
import { RecoveryCards } from "@/components/RecoveryCard";
import { ShareWeek } from "@/components/ShareWeek";
import { ClientChat } from "@/components/ClientChat";
import { VoicePlayer } from "@/components/VoiceRecorder";
import { ClientPayments } from "@/pages/client/Payments";
import { BookSession } from "@/components/BookSession";
import { ReferFriend } from "@/components/ReferFriend";
import { useBrand, Branded, BrandLogo, PoweredBy, brandTagline, brandName } from "@/components/Brand";
import { StatusChip, Empty, Chip } from "@/components/ui-bits";

const photoSrc = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/progress-photos/${id}?auth=${localStorage.getItem("ip_token")}`;
const TABS = ["home", "library", "progress", "reports", "payments"];

const NEXT_LABEL = {
  CONFIRMED: ["ok", "Confirmed"], AWAITING_CONFIRMATION: ["warn", "Action needed — confirm or change"],
  CHANGE_REQUESTED: ["bad", "Change requested — coach notified"], SCHEDULED: ["gray", "Scheduled"],
};

export default function ClientPortal() {
  const [p, setP] = useState(null);
  const [exercises, setExercises] = useState(null);
  const [tab, setTab] = useState(() => (TABS.includes(window.location.hash.slice(1)) ? window.location.hash.slice(1) : "home"));
  const brand = useBrand();
  const [logging, setLogging] = useState(false);
  const [metricType, setMetricType] = useState("SQUAT_5RM");
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const load = useCallback(() => api.get("/client/me").then((r) => setP(r.data)), []);
  const loadEx = useCallback(() => Promise.all([api.get("/client/me/exercises"), api.get("/client/me/program"), api.get("/client/me/recovery"), api.get("/client/me/checkin")]).then(([e, pr, rc, ci]) => { setExercises(e.data); setProgram(pr.data); setRecovery(rc.data); setCheckin(ci.data.checkin); }), []);
  const [program, setProgram] = useState(null);
  const [recovery, setRecovery] = useState([]);
  const [checkin, setCheckin] = useState(null);
  useEffect(() => { load(); loadEx(); }, [load, loadEx]);
  if (!p) return <div className="center"><span className="spin" /></div>;

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post("/client/me/photo", fd); toast.success("Photo updated"); load(); }
    catch (ex) { toast.error(errMsg(ex)); } finally { setUploading(false); }
  };

  const next = p.next;
  const [cls, label] = NEXT_LABEL[next?.status] ?? ["gray", "—"];
  const types = [...new Set(p.metrics.map((m) => m.type))];
  const series = p.metrics.filter((m) => m.type === (types.includes(metricType) ? metricType : types[0])).slice().reverse();
  const nowIso = new Date().toISOString();
  const upcoming = p.sessions.filter((s) => s.startAt > nowIso && !["CANCELLED", "COMPLETED", "NO_SHOW"].includes(s.status));
  const first = p.user.name.split(" ")[0];
  const prs = p.reports.flatMap((r) => (r.highlights || []).map((h) => ({ ...h, weekStart: r.weekStart }))).slice(0, 6);
  const past = p.sessions.filter((s) => s.status === "COMPLETED" && (s.coachNotes || s.loads?.length)).slice().reverse();
  const prepIds = next?.prepExerciseIds || [];
  const BADGE_ICON = { streak: Flame, pr: Trophy, sessions: Dumbbell, ontrack: CheckCircle2, fullweek: CalendarCheck, sharer: Share2 };

  return (
    <Branded brand={brand} data-testid="client-portal">
    <div data-testid="client-portal" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <div className="topbar">
        <div className="brand">
          <BrandLogo brand={brand} size={36} />
          <div className="avatar-upload" onClick={() => !uploading && fileRef.current?.click()} title="Change your photo" data-testid="client-photo-upload-button">
            <Avatar client={{ id: p.id, photoKey: p.photoKey, name: p.user.name }} size={38} radius={10} testId="client-portal-avatar" />
            <span className="cam">{uploading ? <span className="spin" style={{ width: 9, height: 9, borderTopColor: "#fff" }} /> : <Camera size={10} />}</span>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadPhoto} data-testid="client-photo-file-input" />
          </div>
          <div><b data-testid="portal-studio-name">{brandName(brand).toUpperCase()}</b><small data-testid="client-portal-name">{p.user.name.toUpperCase()}</small></div>
          <LookTag scheduled={brand?.scheduled} /></div>
        <SignOut />
      </div>
      <nav className="nav" data-testid="portal-nav">
        {[["home", "Home"], ["library", `Exercise Library${prepIds.length ? " •" : ""}`], ["progress", "Progress"], ["reports", "Reports"], ["payments", "Payments"]].map(([k, l]) =>
          <a key={k} href={`#${k}`} className={tab === k ? "on" : ""} onClick={(e) => { e.preventDefault(); setTab(k); window.location.hash = k; }} data-testid={`portal-tab-${k}`}>{l}</a>)}
      </nav>
      <div className="wrap fade" style={{ flex: 1, width: "100%" }}>
        {p.stats?.badges?.length > 0 && (
          <div className="badges" data-testid="client-badges">
            {p.stats.badges.map((b) => { const Ic = BADGE_ICON[b.kind] || Trophy; return (
              <div className={`badge ${b.kind === "sharer" ? "gold-badge" : ""}`} key={b.kind} data-testid={`badge-${b.kind}`}><span className="ic"><Ic size={14} /></span><span><b>{b.label}</b><small>{b.desc}</small></span></div>); })}
          </div>
        )}
        {tab === "payments" && <ClientPayments />}
        {tab === "library" && (
          <div className="panel" data-testid="portal-library">
            <h3>Exercise Library · assigned by your coach{prepIds.length ? " · highlighted = watch before your next session" : ""}</h3>
            <ClientLibrary exercises={exercises || []} onChanged={loadEx} prepIds={prepIds} />
          </div>
        )}
        {tab === "progress" && (<>
          <BadgeWall clientId={p.id} streak={p.streak} studio={brandName(brand)} />
          <div className="panel" data-testid="portal-progress">
            <div className="ph"><h3>Your progress</h3><button className="btn sm g" onClick={() => setLogging(true)} data-testid="client-log-metric-button-2">Log metric</button></div>
            <div className="tabs">{types.map((t) => <button key={t} className={(types.includes(metricType) ? metricType : types[0]) === t ? "on" : ""} onClick={() => setMetricType(t)}>{t.replace("_", " ")}</button>)}</div>
            <ProgressChart values={series.map((m) => m.value)} unit={series[0]?.unit || ""} />
            <MyTrends />
            <h3 style={{ marginTop: 16 }}>Progress photos</h3>
            <ProgressPhotos photos={p.progressPhotos} uploadPath="/client/me/progress-photos" onChanged={load} />
            <h3 style={{ marginTop: 16 }}>Coach notes from past sessions</h3>
            {past.length === 0 && <Empty>Your coach's workout notes and loads will appear here after sessions.</Empty>}
            {past.map((s) => (
              <div className="excard" key={s.id} data-testid={`session-notes-${s.id}`}>
                <div className="st">{s.title} <span className="ss">· {fmt(s.startAt, { weekday: "short", day: "numeric", month: "short" })}</span></div>
                {s.coachNotes && <div className="txt" style={{ fontSize: 13.5, marginTop: 4, whiteSpace: "pre-wrap" }}>{s.coachNotes}</div>}
                {s.loads?.length > 0 && <table className="tbl" style={{ marginTop: 6 }}><tbody>{s.loads.map((r, i) => <tr key={i}><td><b>{r.exercise}</b></td><td className="num">{r.sets && `${r.sets} ×`} {r.reps}</td><td className="num">{r.load}</td></tr>)}</tbody></table>}
              </div>
            ))}
          </div>
        </>)}
        {tab === "reports" && (
          <div className="panel" data-testid="portal-reports">
            <h3>Weekly reports · logged &amp; kept</h3>
            {p.reports.length === 0 && <Empty>Your first report arrives Sunday night.</Empty>}
            {p.reports.map((r) => <ReportRow key={r.id} r={r} />)}
          </div>
        )}
        {tab === "home" && <>
        <StreakCard streak={p.streak} first={first} name={p.user.name} studio={brandName(brand)} clientId={p.id} />
        <StreakLeaderboard />
        <div className="msg" data-testid="motivation-quote">
          <div className="who"><span>Daily motivation</span><span>{brandTagline(brand)}</span></div>
          <div className="txt">“{p.quote || `Show up today, ${first}.`}”</div>
        </div>
        <div className="ph" style={{ marginBottom: 8 }}><h3 style={{ margin: 0 }}>Hey {first} — your week</h3>
          <ShareWeek brand={brand} data={{ name: first, program, checkin, badges: p.stats?.badges || [], weekDone: p.weekDone, weekTotal: p.weekTotal, weekStart: program?.weekStart || new Date().toISOString() }} /></div>
        <RecoveryCards items={recovery} onChanged={(r) => { setRecovery(r.recovery); if (r.program) { if (program && r.program.fullWeek !== program.fullWeek) load(); setProgram(r.program); } }} />
        {program && <WeeklyPlan program={program} onChanged={(r) => { setProgram(r); if (r.fullWeek !== program.fullWeek) load(); }} onWatch={() => { setTab("library"); window.location.hash = "library"; }} />}
        <CheckInCard onChanged={setCheckin} />
        <div className="grid2">
          <div>
          <BookSession onBooked={load} />
          <div className="panel">
            <h3>Your next session</h3>
            {next ? (
              <>
                <div className="sess" style={{ borderBottom: 0, paddingTop: 0 }} data-testid="next-session">
                  <div className="t num" style={{ width: 170 }}>{fmt(next.startAt, { weekday: "long", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                  <div className="sb">
                    <div className="st">{next.title}</div>
                    <div className="ss">60 min · with Coach Alex{next.focus ? ` · ${next.focus}` : ""}</div>
                    <span className={`chip ${cls}`} style={{ marginTop: 4 }} data-testid="next-session-status"><span className="dot" />{label}</span>
                  </div>
                </div>
                <div style={{ marginTop: 10 }}><SessionActions session={next} onDone={load} /></div>
              </>
            ) : <Empty>No upcoming session — your coach will schedule your next block.</Empty>}
            <div className="msg" style={{ marginTop: 16 }} data-testid="prep-note">
              <div className="who"><span>Tonight · 8:00 PM — session prep</span></div>
              <div className="txt">Focus: {next?.title ?? "your next session"}. {next?.prepNote || "Hydrate tonight, lay out your gear, run the 8-min hip & hamstring flow, sleep 7+ hours."}</div>
              {prepIds.length > 0 && (
                <div style={{ marginTop: 8 }} data-testid="prep-exercises">
                  <div className="ss" style={{ marginBottom: 4 }}>Watch before your session:</div>
                  <div className="actions">{(exercises || []).filter((e) => prepIds.includes(e.id)).map((e) => <button key={e.id} className="btn sm" onClick={() => { setTab("library"); window.location.hash = "library"; }} data-testid={`prep-ex-link-${e.id}`}>▶ {e.name}{e.watchedAt ? " ✓" : ""}</button>)}</div>
                </div>
              )}
            </div>
            <h3 style={{ marginTop: 16 }}>Upcoming · {upcoming.length}</h3>
            {upcoming.length === 0 && <Empty>Nothing else booked yet.</Empty>}
            {upcoming.map((s, i) => (
              <div className="sess" key={s.id} data-testid={`my-session-${s.id}`}>
                <div className="t num" style={{ width: 40 }}>#{i + 1}</div>
                <div className="t num">{fmt(s.startAt)}</div>
                <div className="sb"><div className="st">{s.title}</div>{s.changeNote && <div className="ss">You asked: {s.changeNote}</div>}</div>
                <StatusChip status={s.status} />
                {s.selfBooked && <span className="chip gray" data-testid={`self-booked-${s.id}`}>{s.seriesId ? "weekly · you" : "booked by you"}</span>}
                <SessionActions session={s} onDone={load} />
                {s.selfBooked && new Date(s.startAt) - Date.now() > 24 * 3600 * 1000 && <button className="btn sm g" onClick={async () => { if (!window.confirm("Cancel this session?")) return; try { await api.delete(`/client/me/sessions/${s.id}`); toast.success("Session cancelled"); load(); } catch (ex) { toast.error(errMsg(ex)); } }} data-testid={`cancel-self-booked-${s.id}`}>Cancel</button>}
              </div>
            ))}
            <p className="ss" style={{ marginTop: 8 }}>On WhatsApp/SMS reply "C#" to confirm, "M# day time" to move, or "CALL".</p>
          </div>
          </div>
          <div>
            <ReferFriend />
            <div className="panel">
              <div className="ph"><h3>Your progress</h3><button className="btn sm g" onClick={() => setLogging(true)} data-testid="client-log-metric-button">Log metric</button></div>
              <div style={{ display: "flex", gap: 24, alignItems: "center", marginBottom: 8 }}>
                <div><div className="big num" data-testid="client-adherence">{p.reports[0]?.adherence ?? 0}%</div><div className="ss">last report adherence</div></div>
                <div><div className="big num" style={{ fontSize: 24 }} data-testid="client-week-done">{p.weekDone}/{p.weekTotal}</div><div className="ss">sessions done this week</div></div>
              </div>
              <div className="tabs">{types.map((t) => <button key={t} className={(types.includes(metricType) ? metricType : types[0]) === t ? "on" : ""} onClick={() => setMetricType(t)} data-testid={`client-metric-tab-${t}`}>{t.replace("_", " ")}</button>)}</div>
              <div className="ss" style={{ margin: "4px 0" }}>{series.length} entries</div>
              <ProgressChart values={series.map((m) => m.value)} unit={series[0]?.unit || ""} testId="client-progress-chart" />
              {prs.length > 0 && (
                <div style={{ marginTop: 10 }} data-testid="client-highlights">
                  <div className="ss" style={{ marginBottom: 4 }}>Recent PRs &amp; milestones</div>
                  <div className="hl">{prs.map((h, i) => <Chip key={i} cls={HL_KIND[h.kind]?.[0]} label={`${h.text} · wk ${fmtDate(h.weekStart)}`} />)}</div>
                </div>
              )}
            </div>
            <ClientChat brand={brand} />
          </div>
        </div>
        <div className="panel">
          <h3>Latest weekly report</h3>
          {p.reports.length === 0 && <Empty>Your first report arrives Sunday night.</Empty>}
          {p.reports.slice(0, 2).map((r) => <ReportRow key={r.id} r={r} />)}
          {p.reports.length > 2 && <button className="linkbtn" onClick={() => { setTab("reports"); window.location.hash = "reports"; }} data-testid="see-all-reports">See all {p.reports.length} reports →</button>}
        </div>
        </>}
      </div>
      <PoweredBy />
      {logging && <MetricForm onClose={() => setLogging(false)} onSaved={() => { setLogging(false); load(); }} />}
    </div>
    </Branded>
  );
}

const LookTag = ({ scheduled }) => {
  if (!scheduled) return null;
  const days = Math.round((new Date(scheduled.endsAt + "T23:59:59") - Date.now()) / 86400000);
  return <span className="chip gray looktag" data-testid="look-countdown">✦ {scheduled.name} look · {days <= 0 ? "ends today" : `${days} day${days !== 1 ? "s" : ""} left`}</span>;
};

const MyTrends = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/client/me/checkins").then((r) => setItems(r.data)); }, []);
  if (!items) return null;
  return <CheckinTrends items={items} title="Your weekly check-ins · last 8 weeks" />;
};

const ReportRow = ({ r }) => (
  <div className="rep" style={{ alignItems: "flex-start" }} data-testid={`client-report-${r.id}`}>
    <div className="rd num">{fmtDate(r.weekStart)}</div>
    <div style={{ flex: 1 }}><div className="st" style={{ fontSize: 13 }}>{r.summary}</div>
      {(r.highlights || []).length > 0 && <div className="hl">{r.highlights.map((h, i) => <Chip key={i} cls={HL_KIND[h.kind]?.[0]} label={h.text} />)}</div>}
      {(r.coachNote || r.voiceNoteId) && <div className="note" style={{ cursor: "default" }} data-testid={`client-note-${r.id}`}><b>From your coach:</b> {r.coachNote || "a voice note"}{r.voiceNoteId && <VoicePlayer id={r.voiceNoteId} testId={`client-report-voice-${r.id}`} />}</div>}
      {r.photoPair?.after && (
        <div className="pair" data-testid={`report-photos-${r.id}`}>
          {r.photoPair.before && <div><img src={photoSrc(r.photoPair.before.id)} alt="before" /><small>Before · {fmtDate(r.photoPair.before.takenAt)}</small></div>}
          <div><img src={photoSrc(r.photoPair.after.id)} alt="after" /><small>{r.photoPair.before ? "Now" : "Latest"} · {fmtDate(r.photoPair.after.takenAt)}</small></div>
        </div>
      )}
    </div>
    <div className="rbar"><i style={{ width: `${r.adherence}%` }} /></div>
    <div className="rv num">{r.adherence}%</div>
  </div>
);
